<template>
    <div class="text-xs-right" :style="{'margin-bottom': (tierPriceHeight + 10) + 'px'}">
        <product-variants
            ref="productVariants"
            v-if="product.variantsIds.length > 0"
            :variants="product && product.variants ? product.variants : []"
            :variants-ids="product && product.variantsIds ? product.variantsIds : []"
        />

        <v-card :class="{'product-buy-box': isVisible, 'elevation-0': !isVisible}">
            <merchants-offers
                v-show="!product.type || product.type != 'coupon'"
                :merchants="productMerchants"
                :product-id="product.id"
                :is-price-minimal="isPriceMinimal"
            />

            <v-layout class="product-buy-box-actions" v-if="showBasketButton" row wrap justify-end ma-0>
                <v-flex class="shrink pa-0 product-buy-box-wishlist-button">
                    <wishlist-button
                        v-if="isVisible && !$vuetify.breakpoint.mdOnly"
                        :productId="product.id"
                        :showButton="true"
                        :showText="true"
                        :isNotAbsolute="true"
                        :disableTooltip="true"
                        :buttonAsLink="$wishlist.isFavorite(product.id)"
                        :buttonColor="'primary'"
                        icon-size="20"
                        :small-button="true"
                        button-class="mx-0"
                    />
                </v-flex>

                <v-flex class="pa-0 shrink pr-0 product-buy-box-amount">
                    <amount-input
                        class="product-buy-box-amount-input"
                        :value.sync="productQuantity"
                        :product="product"
                        :current-merchant-id="currentMerchantId"
                        :current-product-id="currentProductId"
                        :bordered="false"
                        tier-price-absolute
                        :tier-price-height.sync="tierPriceHeight"
                    />
                </v-flex>

                <v-flex
                    class="pa-0 pl-0 grow"
                    :class="$vuetify.breakpoint.xs ? 'pt-0' : ''"
                >
                    <basket-button
                        :amount-value="productQuantity"
                        :product="product"
                        depressed
                        :round="false"
                        :button-text="$vuetify.breakpoint.mdOnly || $vuetify.breakpoint.xsOnly ? 'in the cart mobile' : undefined"
                        :current-product-id="currentProductId"
                        @selectVariant="scrollToVariants"
                    />
                </v-flex>
            </v-layout>
        </v-card>
        <!-- Hidden for presentation. Contact form & sharing buttons don't work currently.
        <product-contact-form />
            
        <v-layout
            row
            wrap
        >
            <v-flex xs12 sm6>
                <sharing-buttons />
            </v-flex>
        </v-layout>
        -->
        <router-link
            :href="product ? '/product/' + product.id : '/'"
            class="pull-right"
            v-if="$store.getters.currentLinkMerchant"
        >
            {{ 'view all offers' | translation }}
        </router-link>

        <static-page-module
            resource="*/product/show"
            area="usp_cards"
        />
    </div>
</template>

<style lang="stylus">
@import '~vuetify/src/stylus/settings/_variables';

.product-buy-box {
    border-color: var(--v-primary-base) !important;
    border: 1px solid var(--v-primary-base);
    border-radius: 5px;

    .product-buy-box-amount {
        border-left: 1px solid var(--v-primary-base);
    }

    .wishlist-button {
        height: 30px;
    }

    .merchant-offer {
        border-bottom: 1px solid var(--v-primary-base);
    }
}

@media(max-width: $grid-breakpoints.md) {
    .product-buy-box-amount-input {
        .product-tier-prices-box {
            left: -30px !important;
        }
    }
}

@media(min-width: $grid-breakpoints.lg) {
    .product-buy-box-amount-input {
        .product-tier-prices-box {
            left: -30px !important;
        }
    }
}
</style>

<script>
import ProductVariants from './ProductVariants';
import AmountInput from './AmountInput';
import BasketButton from './BasketButton';
import SharingButtons from './SharingButtons';
import ProductContactForm from './ProductContactForm';

import {createNamespacedHelpers} from 'vuex';

const parentModuleName = 'Product';
const {mapState, mapGetters, mapMutations} = createNamespacedHelpers(parentModuleName);

export default {
    components: {
        ProductVariants,
        AmountInput,
        BasketButton,
        SharingButtons,
        ProductContactForm
    },
    props: ['product'],
    created() {
        this.setAmountValue(1);

        const merchants = Object.values(this.product.merchant);
        if (merchants.length > 0) {
            const [merchant] = merchants;
            this.setCurrentMerchantId(`${merchant.id}`);
        }
    },
    data() {
        return {
            tierPriceHeight: 0,
        };
    },
    computed: {
        ...mapState([
            'amountValue',
            'currentProductId',
            'currentMerchantId',
        ]),
        ...mapGetters(['isProductBuyable']),
        productQuantity: {
            get() {
                return this.amountValue;
            },
            set(value) {
                this.setAmountValue(value);
            }
        },
        showBasketButton: () => true,
        isVisible() {
            return !this.currentProductId || this.isProductBuyable(this.product, this.currentMerchantId, this.currentProductId);
        },
        productMerchants() {
            let productMerchants = null;

            if (this.product && this.product.merchant) {
                if (this.currentProductId) {
                    productMerchants = this.product.merchant;
                } else {
                    if (this.product.merchant) {
                        productMerchants = {};
                    }
                    Object.keys(this.product.merchant).forEach(id => {
                        productMerchants[id] = {
                            ...this.product.merchant[id],
                            price: {
                                [this.product.id]: this.product.price,
                            },
                        };
                    });
                }
            }

            return productMerchants;
        },
        isPriceMinimal() {
            let isMinimal = false;
            const minimalPrice = this.product.price.price;

            if (this.product.merchant[this.currentMerchantId] && this.product.merchant[this.currentMerchantId].price) {
                Object.values(this.product.merchant[this.currentMerchantId].price).some(price => {
                    if (price.price > minimalPrice) {
                        isMinimal = true;
                        return true;
                    }
                });
            }

            return isMinimal;
        },
    },
    methods: {
        ...mapMutations([
            'setAmountValue',
            'setCurrentMerchantId',
        ]),
        scrollToVariants() {
            window.scroll({
                top: this.$refs.productVariants.$el.offsetTop,
                left: 0,
                behavior: 'smooth',
            });
        },
    },
    watch: {
        currentProductId() {
            this.productQuantity = 1;
        },
    }
};
</script>
